/* crm-intelligence.jsx — Pipeline + Relationship Intelligence for Phase 3
   Provides: PipelineIntelligenceView
   Self-registers on window. */

const { useState, useEffect, useMemo, useRef, useCallback } = React;

const fmtMoney  = (n) => '$' + (n >= 1_000_000 ? (n / 1_000_000).toFixed(1) + 'M' : n >= 1_000 ? (n / 1_000).toFixed(0) + 'K' : Math.round(n || 0));
const daysAgo   = (iso) => iso ? Math.floor((Date.now() - new Date(iso)) / 86_400_000) : 999;
const daysSince = (iso) => iso ? Math.floor((Date.now() - new Date(iso)) / 86_400_000) : null;

/* ── Small stat box ── */
const StatBox = ({ label, value, color }) => (
  <div style={{ background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 10, padding: '12px 16px', flex: 1, minWidth: 130 }}>
    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 4, fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 800, color: color || 'var(--nx-text)' }}>{value}</div>
  </div>
);

/* ── Section header ── */
const SecHead = ({ icon, title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
    <NexusIcon name={icon} size={14} style={{ color: 'var(--nx-accent)' }} />
    <span style={{ fontWeight: 700, fontSize: 13 }}>{title}</span>
  </div>
);

/* ══════════════════════════════════════════════════════════════
   PIPELINE TAB
   ══════════════════════════════════════════════════════════════ */
const PipelineTab = ({ deals, pipeline, onOpenDrawer }) => {
  const stageMetrics = useMemo(() => {
    return pipeline.map(stage => {
      const stageDeals = deals.filter(d => d.stageId === stage.id);
      const totalVal   = stageDeals.reduce((s, d) => s + (d.value || 0), 0);
      const avgAge     = stageDeals.length ? Math.round(stageDeals.reduce((s, d) => s + (d.age || 0), 0) / stageDeals.length) : 0;
      const stalled    = stageDeals.filter(d => (d.age || 0) > 14 || daysAgo(d.stageEnteredAt) > 14);
      return { stage, count: stageDeals.length, totalVal, avgAge, stalledCount: stalled.length };
    }).filter(m => m.count > 0);
  }, [deals, pipeline]);

  const stalledDeals = useMemo(() =>
    deals
      .filter(d => {
        const age = d.age || daysAgo(d.stageEnteredAt);
        return age > 14;
      })
      .sort((a, b) => (b.age || 0) - (a.age || 0))
      .slice(0, 10),
    [deals]
  );

  /* Velocity: closed won deals — avg days from created to won */
  const { avgVelocity, winRate } = useMemo(() => {
    const wonDeals  = (window.__nx_store?.deals || deals).filter(d => {
      const s = pipeline.find(x => x.id === d.stageId);
      return s && /\bwon\b/i.test(s.name);
    });
    const lostDeals = (window.__nx_store?.deals || deals).filter(d => {
      const s = pipeline.find(x => x.id === d.stageId);
      return s && /\blost\b/i.test(s.name);
    });
    const totalClosed = wonDeals.length + lostDeals.length;
    const wr = totalClosed ? Math.round((wonDeals.length / totalClosed) * 100) : 0;
    const vel = wonDeals.length
      ? Math.round(wonDeals.reduce((s, d) => s + (d.age || daysSince(d.createdAt) || 0), 0) / wonDeals.length)
      : 0;
    return { avgVelocity: vel, winRate: wr };
  }, [deals, pipeline]);

  /* Bottleneck: stage with most stalled */
  const bottleneck = useMemo(() =>
    stageMetrics.reduce((max, m) => m.stalledCount > (max?.stalledCount || 0) ? m : max, null),
    [stageMetrics]
  );

  /* Stage drop-off */
  const dropoff = useMemo(() => {
    if (pipeline.length < 2) return [];
    return pipeline.slice(0, -1).map((s, i) => {
      const curr = deals.filter(d => d.stageId === s.id).length;
      const next = deals.filter(d => d.stageId === pipeline[i + 1]?.id).length;
      const pct  = curr > 0 ? Math.round((next / curr) * 100) : 0;
      return { from: s.name, to: pipeline[i + 1]?.name || '—', pct };
    });
  }, [deals, pipeline]);

  return (
    <div>
      {/* Stage Funnel Metrics */}
      <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)', marginBottom: 20 }}>
        <SecHead icon="Filter" title="Stage Funnel Metrics" />
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                {['Stage', 'Deals', 'Value', 'Avg Age', 'Stalled'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 10px', fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stageMetrics.map(m => (
                <tr key={m.stage.id} style={{ borderTop: '1px solid var(--nx-border)' }}>
                  <td style={{ padding: '8px 10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: m.stage.color || m.stage.tone || 'var(--nx-accent)' }} />
                      <span style={{ fontWeight: 500 }}>{m.stage.name}</span>
                    </div>
                  </td>
                  <td style={{ padding: '8px 10px' }}>{m.count}</td>
                  <td style={{ padding: '8px 10px', fontWeight: 600, color: 'var(--nx-accent)' }}>{fmtMoney(m.totalVal)}</td>
                  <td style={{ padding: '8px 10px' }}>{m.avgAge}d</td>
                  <td style={{ padding: '8px 10px' }}>
                    {m.stalledCount > 0 ? (
                      <span style={{ color: m.stalledCount > 3 ? 'var(--nx-danger)' : '#F59E0B', fontWeight: 600 }}>
                        {m.stalledCount}
                      </span>
                    ) : <span style={{ color: 'var(--nx-text-faint)' }}>—</span>}
                  </td>
                </tr>
              ))}
              {stageMetrics.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 12 }}>No active pipeline data.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Velocity & Conversion */}
        <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)' }}>
          <SecHead icon="Zap" title="Velocity and Conversion" />
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <StatBox label="Avg Deal Velocity" value={avgVelocity + 'd'} color="var(--nx-accent)" />
            <StatBox label="Win Rate" value={winRate + '%'} color={winRate >= 50 ? 'var(--nx-success)' : winRate >= 30 ? '#F59E0B' : 'var(--nx-danger)'} />
          </div>
          {dropoff.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Stage Conversion</div>
              {dropoff.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 11 }}>
                  <span style={{ color: 'var(--nx-text-muted)', minWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.from}</span>
                  <NexusIcon name="ArrowRight" size={10} style={{ color: 'var(--nx-text-faint)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--nx-text-muted)', minWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.to}</span>
                  <div style={{ flex: 1, height: 4, background: 'var(--nx-border)', borderRadius: 2 }}>
                    <div style={{ width: d.pct + '%', height: '100%', background: d.pct >= 50 ? 'var(--nx-success)' : d.pct >= 25 ? '#F59E0B' : 'var(--nx-danger)', borderRadius: 2 }} />
                  </div>
                  <span style={{ fontSize: 10, color: 'var(--nx-text-muted)', minWidth: 28, textAlign: 'right' }}>{d.pct}%</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottleneck Alert */}
        <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: `1px solid ${bottleneck?.stalledCount > 0 ? '#FCA5A5' : 'var(--nx-border)'}` }}>
          <SecHead icon="AlertTriangle" title="Bottleneck Alert" />
          {bottleneck && bottleneck.stalledCount > 0 ? (
            <div>
              <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--nx-danger)' }}>{bottleneck.stage.name}</div>
                <div style={{ fontSize: 12, color: '#B91C1C', marginTop: 4 }}>
                  {bottleneck.stalledCount} deal{bottleneck.stalledCount > 1 ? 's' : ''} stalled for more than 14 days
                </div>
              </div>
              <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', lineHeight: 1.5, margin: 0 }}>
                This stage has the highest concentration of stalled deals. Consider reviewing these deals and taking action to advance or disqualify them.
              </p>
            </div>
          ) : (
            <EmptyState icon="CheckCircle" title="No bottlenecks detected" desc="All stages are flowing normally with fewer than 14 days average age." compact />
          )}
        </div>
      </div>

      {/* Stalled Deals */}
      <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)' }}>
        <SecHead icon="Clock" title="Stalled Deals (>14 days in stage)" />
        {stalledDeals.length === 0 ? (
          <EmptyState icon="CheckCircle" title="No stalled deals" desc="All deals have had recent activity." compact />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {stalledDeals.map(d => {
              const stage    = pipeline.find(s => s.id === d.stageId);
              const age      = d.age || daysAgo(d.stageEnteredAt);
              const ageColor = age > 30 ? 'var(--nx-danger)' : '#F59E0B';
              return (
                <button key={d.id} className="nx-prelated-row" style={{ background: 'var(--nx-surface)', borderRadius: 8 }} onClick={() => onOpenDrawer(d)}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--nx-accent-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <NexusIcon name="Target" size={13} style={{ color: 'var(--nx-accent)' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <div style={{ fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {stage && <Pill tone="blue">{stage.name}</Pill>}
                      <span style={{ fontSize: 11, color: ageColor, fontWeight: 600 }}>{age}d stalled</span>
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--nx-accent)', flexShrink: 0 }}>{fmtMoney(d.value)}</div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   RELATIONSHIPS TAB
   ══════════════════════════════════════════════════════════════ */
const RelationshipsTab = ({ companies, people, deals, activity }) => {
  const KEY_TITLES = ['CEO', 'CFO', 'CTO', 'COO', 'CMO', 'VP', 'Director', 'Head of', 'Manager'];

  const companiesWithScore = useMemo(() => {
    return companies.map(co => {
      const coContacts   = people.filter(p => p.companyId === co.id || p.company === co.name);
      const coActivities = activity.filter(a => a.companyId === co.id);
      const coDeals      = deals.filter(d => d.companyId === co.id);
      const score        = Math.min(100, coContacts.length * 5 + coActivities.length * 3 + coDeals.length * 10);
      const lastAct      = coActivities[0];
      return { co, contacts: coContacts, activities: coActivities, deals: coDeals, score, lastAct };
    }).sort((a, b) => b.score - a.score);
  }, [companies, people, deals, activity]);

  const keyContacts = useMemo(() =>
    people.filter(p => {
      const r = (p.role || p.title || '').toLowerCase();
      return KEY_TITLES.some(t => r.includes(t.toLowerCase()));
    }).slice(0, 12),
    [people]
  );

  const companyTimeline = useMemo(() => {
    const grouped = {};
    activity.slice(0, 50).forEach(a => {
      const co = companies.find(c => c.id === a.companyId);
      const key = co?.name || 'Unknown Company';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(a);
    });
    return Object.entries(grouped).slice(0, 8);
  }, [activity, companies]);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Key Contacts */}
        <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)' }}>
          <SecHead icon="Star" title="Key Contacts" />
          {keyContacts.length === 0 ? (
            <EmptyState icon="Users" title="No key contacts identified" desc="Contacts with executive titles are highlighted here." compact />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {keyContacts.map(p => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: 'var(--nx-surface)', borderRadius: 6 }}>
                  <Avatar name={p.name} size={28} tone="var(--color-primary-500)" style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--nx-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.role || p.title}{p.company ? ' · ' + p.company : ''}</div>
                  </div>
                  <Pill tone="purple">Key</Pill>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Relationship Scores */}
        <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)' }}>
          <SecHead icon="TrendingUp" title="Relationship Score by Account" />
          {companiesWithScore.length === 0 ? (
            <EmptyState icon="Building2" title="No companies" desc="Add companies to see relationship scores." compact />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {companiesWithScore.slice(0, 8).map(({ co, score, contacts, deals: coDeals }) => {
                const sc = score >= 70 ? 'var(--nx-success)' : score >= 40 ? '#F59E0B' : 'var(--nx-danger)';
                return (
                  <div key={co.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{co.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--nx-text-muted)' }}>{contacts.length} contacts · {coDeals.length} deals</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 50, height: 4, background: 'var(--nx-border)', borderRadius: 2 }}>
                        <div style={{ width: score + '%', height: '100%', background: sc, borderRadius: 2 }} />
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: sc, minWidth: 24 }}>{score}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Contact Influence Map */}
      <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)', marginBottom: 20 }}>
        <SecHead icon="Network" title="Contact Influence by Account" />
        {companiesWithScore.length === 0 ? (
          <EmptyState icon="Users" title="No data" desc="Add companies and contacts to see influence mapping." compact />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {companiesWithScore.slice(0, 5).map(({ co, contacts }) => (
              contacts.length > 0 && (
                <div key={co.id}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', marginBottom: 6 }}>{co.name}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {contacts.map(p => {
                      const isKey = KEY_TITLES.some(t => (p.role || '').toLowerCase().includes(t.toLowerCase()));
                      return (
                        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', background: isKey ? 'var(--nx-accent-tint)' : 'var(--nx-surface)', border: `1px solid ${isKey ? 'var(--nx-accent)' : 'var(--nx-border)'}`, borderRadius: 20 }}>
                          <Avatar name={p.name} size={18} tone="var(--color-primary-500)" />
                          <span style={{ fontSize: 11, fontWeight: isKey ? 600 : 400 }}>{p.name}</span>
                          {isKey && <NexusIcon name="Star" size={9} style={{ color: 'var(--nx-accent)' }} />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </div>

      {/* Interaction Timeline */}
      <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)' }}>
        <SecHead icon="Activity" title="Interaction Timeline by Account" />
        {companyTimeline.length === 0 ? (
          <EmptyState icon="Activity" title="No interactions recorded" desc="Activity logged against companies will appear here." compact />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {companyTimeline.map(([coName, acts]) => (
              <div key={coName}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', marginBottom: 6 }}>{coName}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {acts.slice(0, 3).map(a => {
                    const iconMap = { email: 'Mail', call: 'Phone', meeting: 'Calendar', note: 'StickyNote', whatsapp: 'MessageCircle' };
                    return (
                      <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                        <NexusIcon name={iconMap[a.type] || 'Activity'} size={11} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
                        <span style={{ color: 'var(--nx-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.what || a.type}</span>
                        <span style={{ color: 'var(--nx-text-faint)', marginLeft: 'auto', flexShrink: 0 }}>{a.time || '—'}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   PIPELINE INTELLIGENCE VIEW (top-level with tabs)
   ══════════════════════════════════════════════════════════════ */
const PipelineIntelligenceView = () => {
  const store = useStore();
  const wsId  = window.__nx_auth?.user?.activeWorkspaceId;
  const [tab, setTab] = useState('pipeline');

  const pipeline  = store.pipeline  || [];
  const companies = store.companies || [];
  const people    = store.people    || [];
  const activity  = store.activity  || [];

  /* Active (non-won/lost) deals only for pipeline analysis */
  const activeDeals = useMemo(() =>
    (store.deals || []).filter(d => {
      const s = pipeline.find(x => x.id === d.stageId);
      return s && !/\b(won|lost)\b/i.test(s.name);
    }),
    [store.deals, pipeline]
  );

  const handleOpenDrawer = useCallback((d) => {
    store.openDrawer?.(d);
  }, [store]);

  const TABS = [
    { id: 'pipeline',      label: 'Pipeline',      icon: 'Filter' },
    { id: 'relationships', label: 'Relationships',  icon: 'Network' },
  ];

  return (
    <div style={{ padding: 'var(--space-5)', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Pipeline Intelligence</h1>
        <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', margin: '4px 0 0' }}>Data-driven insights from your live pipeline and relationship graph.</p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--nx-border)', marginBottom: 20 }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              fontSize: 13, fontWeight: tab === t.id ? 700 : 500,
              color: tab === t.id ? 'var(--nx-accent)' : 'var(--nx-text-muted)',
              background: 'transparent', border: 'none', cursor: 'pointer',
              borderBottom: tab === t.id ? '2px solid var(--nx-accent)' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            <NexusIcon name={t.icon} size={13} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'pipeline' && (
        <PipelineTab
          deals={activeDeals}
          pipeline={pipeline}
          onOpenDrawer={handleOpenDrawer}
        />
      )}
      {tab === 'relationships' && (
        <RelationshipsTab
          companies={companies}
          people={people}
          deals={store.deals || []}
          activity={activity}
        />
      )}
    </div>
  );
};
window.PipelineIntelligenceView = PipelineIntelligenceView;
